// CustomerInfoForm.jsx
import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import './CustomerInfoForm.css';
import { validateEmail, validatePhone, validateRequired, formatPhoneOnChange } from '../utils/ValidationUtils';

const CustomerInfoForm = ({ 
  customerInfo, 
  setCustomerInfo, 
  onSubmit,
  onCancel,
  isSubmitting 
}) => {
  // State for form errors
  const [errors, setErrors] = useState({
    name: '',
    phone: '',
    email: ''
  });

  // Handle input changes with validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone numbers - format as they type
    let processedValue = value;
    if (name === 'phone') {
      processedValue = formatPhoneOnChange(value);
    }
    
    // Update customer info
    setCustomerInfo(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear the specific error when the user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validate name (required)
    const nameValidation = validateRequired(customerInfo.name, 'Name');
    if (!nameValidation.valid) {
      newErrors.name = nameValidation.message;
      isValid = false;
    }
    
    // Validate phone (required)
    const phoneValidation = validatePhone(customerInfo.phone);
    if (!phoneValidation.valid) {
      newErrors.phone = phoneValidation.message;
      isValid = false;
    }
    
    // Validate email (optional)
    if (customerInfo.email) {
      const emailValidation = validateEmail(customerInfo.email);
      if (!emailValidation.valid) {
        newErrors.email = emailValidation.message;
        isValid = false;
      }
    }
    
    // Update error state
    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission with validation
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all fields before proceeding
    if (validateForm()) {
      onSubmit();
    }
  };

  // Handle field blur for immediate validation feedback
  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Validate based on field type
    switch (name) {
      case 'name':
        const nameValidation = validateRequired(value, 'Name');
        if (!nameValidation.valid) {
          setErrors(prev => ({ ...prev, name: nameValidation.message }));
        }
        break;
      
      case 'phone':
        const phoneValidation = validatePhone(value);
        if (!phoneValidation.valid) {
          setErrors(prev => ({ ...prev, phone: phoneValidation.message }));
        }
        break;
      
      case 'email':
        if (value) {
          const emailValidation = validateEmail(value);
          if (!emailValidation.valid) {
            setErrors(prev => ({ ...prev, email: emailValidation.message }));
          }
        }
        break;
      
      default:
        break;
    }
  };

  return (
    <div className="customer-info-form">
      <div className="form-header">
        <h3>Your Information</h3>
        <p>Please provide your contact details to complete your order</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
          <label htmlFor="name">Name <span className="required">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            value={customerInfo.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Your full name"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "name-error" : undefined}
          />
          {errors.name && (
            <div className="error-message" id="name-error">{errors.name}</div>
          )}
        </div>

        <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
          <label htmlFor="phone">Phone Number <span className="required">*</span></label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={customerInfo.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Your contact number"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "phone-error" : undefined}
          />
          {errors.phone && (
            <div className="error-message" id="phone-error">{errors.phone}</div>
          )}
        </div>

        <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
          <label htmlFor="email">Email Address <span className="optional">(optional)</span></label>
          <input
            type="email"
            id="email"
            name="email"
            value={customerInfo.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Your email address"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && (
            <div className="error-message" id="email-error">{errors.email}</div>
          )}
        </div>

        <div className="form-actions">
          <Button 
            type="button" 
            className="cancel-button"
            onClick={onCancel}
          >
            Back to Cart
          </Button>
          
          <Button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Continue to Payment'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerInfoForm;